import { useEffect, useRef } from 'react';
import { useSessionStore } from '../stores/session/sessionStore';
import { requestPortalRenonce } from '../utils/portal/portalRenonce';

// The server-side AcceoConnect session goes cold after ~60 min idle (no rolling-window refresh while
// the tab is hidden). If the tab was hidden longer than this, recover proactively on return — before
// the user acts — so the first action doesn't fail and a recovery reload never lands mid-action.
const COLD_AFTER_HIDDEN_MS = 50 * 60 * 1000;

/**
 * Proactively triggers a portal re-nonce when the user returns to a tab that was hidden long enough
 * for the AcceoConnect session to have gone cold. Portal sessions only — desktop self-renews via OIDC.
 */
export function usePortalResumeRecovery(): void {
    const hiddenAtRef = useRef<number | null>(null);

    useEffect(() => {
        function onVisibilityChange(): void {
            if (document.visibilityState === 'hidden') {
                hiddenAtRef.current = Date.now();
                return;
            }
            const hiddenAt = hiddenAtRef.current;
            hiddenAtRef.current = null;
            if (hiddenAt === null) return;

            const isPortal = useSessionStore.getState().sessionKind === 'portal';
            if (isPortal && Date.now() - hiddenAt >= COLD_AFTER_HIDDEN_MS) {
                requestPortalRenonce();
            }
        }

        document.addEventListener('visibilitychange', onVisibilityChange);
        return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }, []);
}
